import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const fadeInUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const CybersecurityServices = () => {
  const canvasRef = useRef(null);
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    if (window.innerWidth < 768) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const resizeCanvas = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resizeCanvas();
    class NetworkNode {
      constructor() { this.x = Math.random()*canvas.width; this.y = Math.random()*canvas.height; this.vx = (Math.random()-0.5)*0.8; this.vy = (Math.random()-0.5)*0.8; this.radius = Math.random()*2.5+1; this.pulsePhase = Math.random()*Math.PI*2; }
      update() { this.x+=this.vx; this.y+=this.vy; if(this.x<0)this.x=canvas.width; if(this.x>canvas.width)this.x=0; if(this.y<0)this.y=canvas.height; if(this.y>canvas.height)this.y=0; this.pulsePhase+=0.03; }
      draw() { const pulse=Math.sin(this.pulsePhase)*0.5+0.5; const size=this.radius+pulse*1.5; ctx.shadowBlur=8+pulse*4; ctx.shadowColor='#00a8c0'; ctx.beginPath(); ctx.arc(this.x,this.y,size,0,Math.PI*2); const g=ctx.createRadialGradient(this.x,this.y,0,this.x,this.y,size); g.addColorStop(0,`rgba(0,210,230,${0.6+pulse*0.15})`); g.addColorStop(0.5,`rgba(0,180,200,${0.4+pulse*0.15})`); g.addColorStop(1,'rgba(0,150,180,0)'); ctx.fillStyle=g; ctx.fill(); ctx.shadowBlur=0; }
    }
    const nodes = Array.from({length:60},()=>new NetworkNode());
    const drawConnections = () => { for(let i=0;i<nodes.length;i++) for(let j=i+1;j<nodes.length;j++) { const dx=nodes[i].x-nodes[j].x,dy=nodes[i].y-nodes[j].y,d=Math.sqrt(dx*dx+dy*dy); if(d<180){ctx.beginPath();ctx.moveTo(nodes[i].x,nodes[i].y);ctx.lineTo(nodes[j].x,nodes[j].y);const op=(1-d/180)*0.2;ctx.strokeStyle=`rgba(0,200,180,${op})`;ctx.lineWidth=1;ctx.stroke();}}};
    const animate = () => { ctx.fillStyle='rgba(10,37,64,0.12)'; ctx.fillRect(0,0,canvas.width,canvas.height); nodes.forEach(n=>{n.update();n.draw();}); drawConnections(); animationFrameId=requestAnimationFrame(animate); };
    animate();
    window.addEventListener('resize', resizeCanvas);
    return () => { window.removeEventListener('resize', resizeCanvas); cancelAnimationFrame(animationFrameId); };
  }, []);

  const services = [
    {
      icon: '🛡️',
      color: '#00d4ff',
      title: 'Antiphishing & Cyber Frauds',
      shortDesc: 'AI-powered defense against phishing attacks and cyber fraud attempts targeting your organization.',
      features: ['AI-powered phishing detection & prevention', 'Real-time threat intelligence feeds', 'Simulated phishing campaign management', 'Employee awareness training integration', 'Incident response & forensic support', 'Dark web monitoring for credential leaks'],
      useCases: ['Financial institutions protecting customer accounts', 'Enterprises defending against BEC attacks', 'Government agencies securing communications'],
      stats: [{ v: '99.7%', l: 'Detection Rate' }, { v: '<1ms', l: 'Response Time' }, { v: '500+', l: 'Threats Blocked Daily' }],
    },
    {
      icon: '🔍',
      color: '#a855f7',
      title: 'Data Leakage Detection & Prevention',
      shortDesc: 'Next-generation AI analytics to detect, monitor, and prevent unauthorized data exfiltration.',
      features: ['AI-based content inspection & classification', 'Endpoint DLP agent deployment', 'Cloud data protection (AWS, Azure, GCP)', 'Network traffic analysis & monitoring', 'Automated policy enforcement', 'Compliance reporting (GDPR, ISO 27001)'],
      useCases: ['Healthcare organizations protecting patient data', 'Legal firms securing client documents', 'R&D teams protecting intellectual property'],
      stats: [{ v: '360°', l: 'Visibility' }, { v: '50+', l: 'Data Types Monitored' }, { v: '0', l: 'Data Breach Tolerance' }],
    },
    {
      icon: '🎯',
      color: '#00ff80',
      title: 'Penetration Testing',
      shortDesc: 'Real-world attack simulation across your applications, networks and telecom infrastructure, delivered with clear, actionable remediation.',
      features: ['Web Application Penetration Testing (OWASP Top 10, business logic, authentication)', 'Mobile App Security Testing (Android & iOS, static & dynamic analysis)', 'API & Microservices Testing (REST, GraphQL, SOAP)', '4G / 5G Telecom Security Testing (core network, RAN, SIM/USIM, signalling)', 'Network & Infrastructure Testing (internal, external, Active Directory)', 'Cloud Security Assessment (AWS, Azure, GCP misconfigurations)', 'Wireless & Wi-Fi Testing (WPA2/3, rogue AP, evil twin)', 'IoT, Firmware & Embedded Testing (hardware interfaces, OTA, protocols)', 'OT / ICS / SCADA Assessment', 'Red Team & Social Engineering', 'Container & Kubernetes Security', 'Source Code Review (SAST-assisted)'],
      useCases: ['Enterprises validating security before audits', 'Telecom providers securing 5G infrastructure', 'Startups ensuring secure product launches'],
      stats: [{ v: 'OWASP', l: 'Aligned Methodology' }, { v: 'CVSS', l: 'Risk-Rated Findings' }, { v: '12+', l: 'Testing Domains' }],
    },
    {
      icon: '💻',
      color: '#f59e0b',
      title: 'Secure Software Development',
      shortDesc: 'End-to-end secure SDLC consulting, code auditing, and DevSecOps integration services.',
      features: ['SAST & DAST integration in CI/CD pipelines', 'Manual code review & security auditing', 'DevSecOps consulting & implementation', 'Vulnerability assessment & management', 'Penetration testing for web & mobile apps', 'Security architecture design & review'],
      useCases: ['SaaS companies shipping secure products', 'Enterprises modernizing legacy systems', 'Startups building security-first applications'],
      stats: [{ v: '1000+', l: 'Vulnerabilities Fixed' }, { v: 'OWASP', l: 'Top 10 Coverage' }, { v: '48hr', l: 'Audit Turnaround' }],
    },
    {
      icon: '🎓',
      color: '#f43f5e',
      title: 'Education & Upskilling',
      shortDesc: 'Professional cybersecurity training programs for students, teams, and corporate organizations.',
      features: ['Corporate cybersecurity awareness programs', 'Hands-on ethical hacking workshops', 'Certification preparation courses', 'Custom curriculum development', 'Live labs & CTF competitions', 'Post-training assessment & certification'],
      useCases: ['IT teams building internal security skills', 'Universities integrating cyber curriculum', 'Corporates meeting compliance training requirements'],
      stats: [{ v: '1000+', l: 'Professionals Trained' }, { v: '95%', l: 'Satisfaction Rate' }, { v: '20+', l: 'Training Modules' }],
    },
    {
      icon: '🤖',
      color: '#00b4d8',
      title: 'Tactical Cyber Security & AI',
      shortDesc: 'Cutting-edge AI-powered cyber defense solutions for niche and high-value threat scenarios.',
      features: ['MITRE ATT&CK framework coverage', 'AI-driven threat hunting & detection', 'SOC-as-a-Service consulting', 'Behavioral analytics & anomaly detection', 'Zero-day vulnerability research', 'Custom cyber defense tool development'],
      useCases: ['Critical infrastructure protection', 'Defense & government sector security', 'High-value target organizations'],
      stats: [{ v: 'MITRE', l: 'ATT&CK Mapped' }, { v: '24/7', l: 'SOC Monitoring' }, { v: 'AI', l: 'Powered Defense' }],
    },
  ];

  const steps = [
    { n: '01', icon: '🔎', title: 'Assessment', desc: 'We conduct a thorough security assessment of your organization to identify vulnerabilities, risks, and gaps in your current security posture.' },
    { n: '02', icon: '🛠️', title: 'Solution Design', desc: 'Our experts design a customized security solution tailored to your specific needs, industry requirements, and compliance obligations.' },
    { n: '03', icon: '🚀', title: 'Implementation', desc: 'We deploy and integrate our solutions seamlessly into your existing infrastructure with minimal disruption to your operations.' },
    { n: '04', icon: '📊', title: 'Monitor & Optimize', desc: 'Continuous monitoring, reporting, and optimization ensures your security posture evolves with the threat landscape.' },
  ];

  const testimonials = [
    { name: 'Rajesh Kumar', role: 'CISO, FinTech Corp', text: 'MartianBlue\'s antiphishing solution reduced our phishing incidents by 94% within the first quarter. Their team is responsive and deeply knowledgeable.' },
    { name: 'Priya Sharma', role: 'IT Head, Healthcare Ltd', text: 'The DLP solution they implemented gave us complete visibility into our data flows. We are now fully HIPAA compliant with zero data breach incidents.' },
    { name: 'Arun Mehta', role: 'CTO, E-Commerce Platform', text: 'Their secure software development consulting transformed our SDLC. We now ship products with security built-in, not bolted-on.' },
  ];

  return (
    <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a2540 0%, #0d3a5c 35%, #115073 65%, #0d3a5c 100%)' }}>
      <canvas ref={canvasRef} className="absolute top-0 left-0 w-full pointer-events-none" style={{ zIndex: 1, height: '100%' }} />
      <div className="relative min-h-screen pt-20" style={{ zIndex: 10 }}>

        {/* Hero */}
        <section className="relative py-20" style={{ background: 'rgba(13,40,71,0.8)' }}>
          <div className="container mx-auto px-4 text-center">
            <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.8 }}>
              <span className="text-sm font-semibold tracking-wider uppercase mb-4 block" style={{ color:'#00b4d8' }}>COMPREHENSIVE CYBER DEFENSE</span>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white" style={{ fontFamily:'Rajdhani,sans-serif' }}>Cybersecurity Solutions</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                Enterprise-grade cybersecurity products, consulting services, and technology development — protecting organizations from evolving cyber threats.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact" className="cyber-button text-lg px-8 py-3 inline-block">Request a Demo</Link>
                <a href="#services" className="px-8 py-3 rounded-lg border-2 font-semibold inline-block transition-all" style={{ borderColor:'#00b4d8', color:'#00b4d8' }}>Explore Services ↓</a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats Bar */}
        <section style={{ background:'rgba(0,212,255,0.06)', borderTop:'1px solid rgba(0,212,255,0.15)', borderBottom:'1px solid rgba(0,212,255,0.15)' }}>
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[{ v:'500+', l:'Clients Protected' },{ v:'99.9%', l:'Uptime Guarantee' },{ v:'24/7', l:'Expert Support' },{ v:'6+', l:'Security Domains' }].map(s => (
                <motion.div key={s.l} initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}>
                  <div className="text-3xl font-bold" style={{ color:'#00d4ff', fontFamily:'Rajdhani,sans-serif' }}>{s.v}</div>
                  <div className="text-gray-400 text-sm mt-1" style={{ fontFamily:'Poppins,sans-serif' }}>{s.l}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="services" className="py-20">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase mb-4 block" style={{ color:'#00b4d8' }}>WHAT WE OFFER</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily:'Rajdhani,sans-serif' }}>Enterprise-Grade Cybersecurity</h2>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto">Click on any service to explore features, use cases, and key metrics</p>
            </motion.div>
            <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once:true }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((s, i) => (
                <motion.div key={i} variants={fadeInUp} onClick={() => setActiveService(activeService===i ? null : i)}
                  className="cyber-card cursor-pointer transition-all duration-300"
                  style={{ border: activeService===i ? `2px solid ${s.color}` : '1px solid rgba(0,212,255,0.2)', boxShadow: activeService===i ? `0 0 30px ${s.color}30` : 'none' }}
                  whileHover={{ y:-8 }}>
                  <div className="text-4xl mb-4">{s.icon}</div>
                  <h3 className="text-xl font-bold mb-3 text-white" style={{ fontFamily:'Rajdhani,sans-serif', color: s.color }}>{s.title}</h3>
                  <p className="text-gray-400 text-sm mb-4" style={{ fontFamily:'Poppins,sans-serif' }}>{s.shortDesc}</p>
                  <AnimatePresence>
                    {activeService===i && (
                      <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:'auto' }} exit={{ opacity:0, height:0 }}>
                        <div className="mt-4 pt-4" style={{ borderTop:`1px solid ${s.color}40` }}>
                          <p className="text-xs font-semibold mb-2" style={{ color: s.color, fontFamily:'Poppins,sans-serif' }}>KEY FEATURES</p>
                          <ul className="space-y-2 mb-4">
                            {s.features.map((f,fi) => (
                              <li key={fi} className="flex items-start gap-2 text-gray-300 text-sm">
                                <span style={{ color: s.color, flexShrink:0 }}>▸</span>{f}
                              </li>
                            ))}
                          </ul>
                          <div className="grid grid-cols-3 gap-2 mb-4">
                            {s.stats.map((st,si) => (
                              <div key={si} className="text-center p-2 rounded-lg" style={{ background:'rgba(0,0,0,0.3)' }}>
                                <div className="font-bold text-sm" style={{ color: s.color, fontFamily:'Rajdhani,sans-serif' }}>{st.v}</div>
                                <div className="text-gray-500 text-xs">{st.l}</div>
                              </div>
                            ))}
                          </div>
                          <Link to="/contact">
                            <button className="w-full py-2 rounded-lg font-semibold text-sm transition-all" style={{ background: s.color, color:'#000', fontFamily:'Poppins,sans-serif' }}>
                              Get a Quote →
                            </button>
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div className="mt-3 text-xs" style={{ color: s.color, fontFamily:'Poppins,sans-serif' }}>
                    {activeService===i ? '▲ Show less' : '▼ View details'}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20" style={{ background:'rgba(0,0,0,0.2)' }}>
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase mb-4 block" style={{ color:'#00b4d8' }}>OUR PROCESS</span>
              <h2 className="text-4xl font-bold text-white" style={{ fontFamily:'Rajdhani,sans-serif' }}>How We Work With You</h2>
            </motion.div>
            <div className="grid md:grid-cols-4 gap-8 relative">
              <div className="hidden md:block absolute top-8 left-1/4 right-1/4" style={{ height:'2px', background:'linear-gradient(90deg,#00d4ff,#a855f7)', zIndex:0 }} />
              {steps.map((step, i) => (
                <motion.div key={i} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay: i*0.15 }} className="text-center relative z-10">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl" style={{ background:'linear-gradient(135deg,rgba(0,212,255,0.2),rgba(168,85,247,0.2))', border:'2px solid rgba(0,212,255,0.4)' }}>
                    {step.icon}
                  </div>
                  <div className="text-xs font-bold mb-2" style={{ color:'#00d4ff', fontFamily:'Poppins,sans-serif', letterSpacing:2 }}>{step.n}</div>
                  <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily:'Rajdhani,sans-serif' }}>{step.title}</h3>
                  <p className="text-gray-400 text-sm" style={{ fontFamily:'Poppins,sans-serif' }}>{step.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase mb-4 block" style={{ color:'#00b4d8' }}>CLIENT SUCCESS</span>
              <h2 className="text-4xl font-bold text-white" style={{ fontFamily:'Rajdhani,sans-serif' }}>What Our Clients Say</h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((t,i) => (
                <motion.div key={i} initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once:true }} transition={{ delay: i*0.1 }} className="cyber-card">
                  <div className="text-3xl mb-4">⭐⭐⭐⭐⭐</div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-6 italic" style={{ fontFamily:'Poppins,sans-serif' }}>"{t.text}"</p>
                  <div>
                    <div className="font-bold text-white" style={{ fontFamily:'Rajdhani,sans-serif' }}>{t.name}</div>
                    <div className="text-sm" style={{ color:'#00b4d8', fontFamily:'Poppins,sans-serif' }}>{t.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div initial={{ opacity:0, scale:0.95 }} whileInView={{ opacity:1, scale:1 }} transition={{ duration:0.6 }} className="cyber-card max-w-4xl mx-auto text-center p-12">
              <h2 className="text-4xl font-bold mb-6 text-white" style={{ fontFamily:'Rajdhani,sans-serif' }}>Protect Your Organization Today</h2>
              <p className="text-xl text-gray-300 mb-8">Schedule a free consultation with our security experts</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact" className="cyber-button text-lg px-8 py-4 inline-block">Get Started Free</Link>
                <Link to="/about" className="bg-transparent border-2 text-lg px-8 py-4 rounded font-semibold inline-block transition-all" style={{ borderColor:'#00b4d8', color:'#00b4d8' }}>Learn About Us</Link>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default CybersecurityServices;
